<template>
  <b-modal
    :visible="value"
    size="xl"
    centered
    :title="`${$t('Pago de AFP')} - ${monthName(period ? period.month : '')} ${period ? period.year : ''}`"
    @change="(val) => $emit('input', val)"
    hide-footer
  >
    <b-overlay :show="loading">
      <b-row class="mb-2">
        <b-col md="6">
          <b-form-group :label="$t('Seleccionar Empresa')" label-for="empresa-select-pago">
            <v-select
              id="empresa-select-pago"
              v-model="selectedEmpresaId"
              :options="empresas"
              :reduce="(e) => e.id"
              label="razon_social"
              :placeholder="$t('Seleccione una empresa')"
              :clearable="false"
              @input="fetchPreview"
            />
          </b-form-group>
        </b-col>
        <b-col md="6" v-if="pagoData">
          <b-card bg-variant="light" class="p-1 mb-0">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <strong>{{ $t('Cuenta Origen') }}:</strong>
                <span class="ml-50">{{ pagoData.cuenta_origen || $t('No configurada') }}</span>
              </div>
              <b-badge :variant="pagoData.cuenta_origen ? 'light-success' : 'light-danger'">
                {{ pagoData.cuenta_origen ? $t('Configurada') : $t('Sin cuenta') }}
              </b-badge>
            </div>
            <div class="text-right mt-50">
              <strong>{{ $t('Total AFP Periodo') }}:</strong>
              <span class="text-primary font-weight-bold ml-50">S/. {{ formatCurrency(pagoData.total) }}</span>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-table
        v-if="pagoData && pagoData.afps"
        :items="pagoData.afps"
        :fields="tableFields"
        show-empty
        responsive
        striped
        small
        class="mb-2"
      >
        <template #cell(remuneracion)="{ item }">
          S/. {{ formatCurrency(item.remuneracion) }}
        </template>
        <template #cell(fondo)="{ item }">
          S/. {{ formatCurrency(item.fondo) }}
        </template>
        <template #cell(seguro)="{ item }">
          S/. {{ formatCurrency(item.seguro) }}
        </template>
        <template #cell(comision)="{ item }">
          S/. {{ formatCurrency(item.comision) }}
        </template>
        <template #cell(total)="{ item }">
          <strong>S/. {{ formatCurrency(item.total) }}</strong>
        </template>

        <template #cell(cuenta_destino)="{ item }">
          <div>
            <div class="font-small-2 font-weight-bold">{{ item.proveedor || '-' }}</div>
            <small class="text-muted">{{ item.cuenta_destino || $t('Sin cuenta activa') }}</small>
          </div>
        </template>

        <template #cell(estado)="{ item }">
          <div v-if="item.enviado">
            <b-badge variant="light-success">
              <feather-icon icon="CheckCircleIcon" class="mr-25" />
              {{ item.enviado.caja || $t('Generado') }}
            </b-badge>
            <div class="font-small-1 text-muted">{{ item.enviado.fecha }}</div>
          </div>
          <div v-else-if="item.errores && item.errores.length > 0">
            <b-badge variant="light-danger" :title="item.errores.join(' | ')">
              <feather-icon icon="AlertCircleIcon" class="mr-25" />
              {{ $t('Con observaciones') }}
            </b-badge>
            <div v-for="(err, idx) in item.errores" :key="idx" class="font-small-1 text-danger">
              • {{ err }}
            </div>
          </div>
          <div v-else-if="item.total <= 0">
            <b-badge variant="light-secondary">
              {{ $t('Sin monto') }}
            </b-badge>
          </div>
          <div v-else>
            <b-badge variant="light-warning">
              <feather-icon icon="ClockIcon" class="mr-25" />
              {{ $t('Pendiente') }}
            </b-badge>
          </div>
        </template>
      </b-table>

      <div class="d-flex justify-content-between align-items-center mt-2">
        <b-button variant="outline-secondary" @click="$emit('input', false)">
          {{ $t('Cerrar') }}
        </b-button>
        <b-button
          v-if="hasPendingAfps"
          variant="success"
          :disabled="isBusy || hasBlockingErrors"
          @click="confirmEnviarPago"
        >
          <feather-icon icon="SendIcon" class="mr-50" />
          {{ $t('Generar Cajas SymSoft') }}
        </b-button>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import PayrollPeriodService from "@/views/rrhh/views/payroll/services/payroll-period.service";

export default {
  name: "PagoAfpModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    period: {
      type: Object,
      default: () => ({}),
    },
    months: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      isBusy: false,
      empresas: [],
      selectedEmpresaId: null,
      pagoData: null,
      tableFields: [
        { key: "afp", label: "AFP" },
        { key: "afiliados", label: "Afiliados" },
        { key: "remuneracion", label: "Remun. Afecta" },
        { key: "fondo", label: "Fondo" },
        { key: "seguro", label: "Seguro" },
        { key: "comision", label: "Comisión" },
        { key: "total", label: "Total" },
        { key: "cuenta_destino", label: "Proveedor / Cuenta" },
        { key: "estado", label: "Estado" },
      ],
    };
  },
  computed: {
    hasPendingAfps() {
      if (!this.pagoData || !this.pagoData.afps) return false;
      return this.pagoData.afps.some((a) => !a.enviado && a.total > 0);
    },
    hasBlockingErrors() {
      if (!this.pagoData || !this.pagoData.afps) return true;
      const pend = this.pagoData.afps.filter((a) => !a.enviado && a.total > 0);
      return pend.some((a) => a.errores && a.errores.length > 0);
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.fetchEmpresas();
      }
    },
  },
  created() {
    if (this.value) {
      this.fetchEmpresas();
    }
  },
  methods: {
    monthName(month) {
      const found = this.months.find((m) => m.id === Number(month));
      return found ? found.label : month;
    },
    formatCurrency(value) {
      return Number(value ?? 0).toFixed(2);
    },
    async fetchEmpresas() {
      try {
        this.loading = true;
        const { data } = await PayrollPeriodService.getEmpresas();
        this.empresas = data;
        if (this.empresas.length > 0 && !this.selectedEmpresaId) {
          this.selectedEmpresaId = this.empresas[0].id;
        }
        if (this.selectedEmpresaId) {
          await this.fetchPreview();
        }
      } catch (error) {
        this.showToastError(
          error.response?.data?.message || "Error al cargar empresas."
        );
      } finally {
        this.loading = false;
      }
    },
    async fetchPreview() {
      if (!this.selectedEmpresaId || !this.period?.id) return;
      try {
        this.loading = true;
        const { data } = await PayrollPeriodService.previewPagoAfp({
          period_id: this.period.id,
          empresa_id: this.selectedEmpresaId,
        });
        this.pagoData = data;
      } catch (error) {
        this.showToastError(
          error.response?.data || "Error al obtener vista previa de AFP."
        );
      } finally {
        this.loading = false;
      }
    },
    async confirmEnviarPago() {
      const confirm = await this.showConfirmSwal(
        this.$t("¿Generar cajas para pago de AFP?"),
        this.$t(
          "Se crearán las cajas de egreso en SymSoft para las AFPs pendientes con monto mayor a cero."
        ),
        {
          confirmButtonText: this.$t("Sí, generar cajas"),
          cancelButtonText: this.$t("Cancelar"),
        }
      );
      if (!confirm.isConfirmed) return;

      try {
        this.isBusy = true;
        this.addPreloader();
        const { data } = await PayrollPeriodService.enviarPagoAfp({
          period_id: this.period.id,
          empresa_id: this.selectedEmpresaId,
        });
        this.showToastSuccess(this.$t("Proceso completado."));
        if (data.pago) {
          this.pagoData = data.pago;
        } else {
          await this.fetchPreview();
        }
      } catch (error) {
        this.showToastError(
          error.response?.data || "Error al generar las cajas de AFP."
        );
      } finally {
        this.isBusy = false;
        this.removePreloader();
      }
    },
  },
};
</script>
